import React from 'react';
import { LuChevronRight, LuLoaderCircle, LuTriangleAlert } from 'react-icons/lu';
import Avatar from '../../common/Avatar';
import Badge from '../../common/Badge';

const statusDisplays = {
    SUCCESS: { label: 'Generated', tone: 'green' },
    GENERATING: { label: 'Generating', tone: 'blue' },
    WARNING: { label: 'Warning', tone: 'amber' },
    FAIL: { label: 'Failed', tone: 'red' },
    NOT_GENERATED: { label: 'Not generated', tone: 'grey' },
};

/**
 * Report status of one student as a badge (Generated, Generating, Warning, Failed, Not generated).
 */
export const StudentReportStatusBadge = ({ status }) => {
    const display = statusDisplays[status] || statusDisplays.NOT_GENERATED;
    return (
        <Badge tone={display.tone}>
            {status === 'GENERATING' && <LuLoaderCircle aria-hidden className="size-3 animate-spin" />}
            {display.label}
        </Badge>
    );
};

/**
 * One student in the class list: name, admission and roll number, and the report status.
 */
export default function StudentReportCard({ student, report, onSelect }) {
    const studentName = student.name || 'Student';
    const status = report?.status || 'NOT_GENERATED';

    const details = [];
    if (student.admissionNo) details.push(`Adm. ${student.admissionNo}`);
    if (student.rollNo) details.push(`Roll ${student.rollNo}`);

    return (
        <button
            type="button"
            onClick={() => onSelect(student, report)}
            className="group flex w-full flex-col gap-2 rounded-xl border border-ui-line bg-ui-card p-3.5 text-left shadow-card transition-colors hover:border-brand-300 hover:bg-ui-brand-soft dark:hover:border-brand-500/40"
        >
            <div className="flex w-full items-start gap-3">
                <Avatar name={studentName} size="sm" />
                <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-ui-ink" title={studentName}>{studentName}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                        <StudentReportStatusBadge status={status} />
                        {details.length > 0 && <span className="truncate text-xs text-ui-ink-3">{details.join(' · ')}</span>}
                    </div>
                </div>
                <LuChevronRight aria-hidden className="mt-2 size-4 shrink-0 text-ui-ink-3 transition-transform group-hover:translate-x-0.5" />
            </div>

            {status === 'WARNING' && report?.error?.userMessage && (
                <p className="flex items-center gap-1.5 text-xs text-amber-800 dark:text-amber-300">
                    <LuTriangleAlert aria-hidden className="size-3.5 shrink-0" />
                    <span className="truncate">{report.error.userMessage}</span>
                </p>
            )}
        </button>
    );
}
